// REL_1.1.14 F-262 / S-981 — Ported from deedo-frontend MarketingPricingPage.tsx.
// REL_1.1.15 F-262 §2 — currency display via lib/currency.ts (deploy-time-fixed FX, no live forex).
// REL_1.1.15E F-313 (S-1228, 2026-07-09): US-primary now → USD is the single currency; CAD hidden
//   (AUD already removed E-120). The CurrencySelector self-hides with one currency. Reversible.
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Check, Minus } from "lucide-react";
import { useCurrencySettings } from "@/hooks/useCurrencySettings";
import { convertFromUSD, formatCurrency, type CurrencyCode } from "@/lib/currency";
import CurrencySelector from "@/components/common/CurrencySelector";

const MARKETING_BASE = import.meta.env.VITE_MARKETING_BASE_URL || "https://www.deedo.ai";
const APP_BASE_URL: string = import.meta.env.VITE_APP_BASE_URL || "https://app.deedo.ai";

// REL_1.1.15D E-121 (S-1211) — single $40/mo plan (was 3-tier Starter/Professional/Brokerage).
// USD baseline (single currency per F-313 hide-Canada; CAD/AUD removed — see lib/currency.ts).
const PLAN_USD_PER_MONTH = 40;

type Feature = { label: string; included: boolean };

interface Plan {
  name: string;
  desc: string;
  /** USD baseline amount; null = non-monetary (Free / Custom). */
  priceUsd: number | null;
  /** Static label override when priceUsd is null (e.g., "Free", "Custom"). */
  priceLabel?: string;
  unit?: string;
  features: Feature[];
  cta: string;
  ctaHref: string;
  ctaExternal: boolean;
  featured?: boolean;
}

// REL_1.1.15D E-121 (S-1211): one plan at $40/mo (Starter/Free + Brokerage/Custom removed).
// Architecture rulings 2026-06-16: free entry-point is now a TRIAL (a period on the $40 plan, not a
// separate tier); credit line UNBLOCKED = $40 INCLUDES 1,000 AI credits/mo + $10 top-up; plan name is
// NEUTRAL (not "Professional" — no tier ladder). All wording is Documentation/Marketing's to finalize.
const PLANS: Plan[] = [
  {
    name: "Deedo",
    desc: "Start with a free trial, then $40/mo per property.",
    priceUsd: PLAN_USD_PER_MONTH,
    unit: "/month / property",
    features: [
      { label: "1,000 AI credits / month included", included: true },
      { label: "Top-up credits anytime ($10)",      included: true },
      { label: "Advanced CRM Integrations",         included: true },
      { label: "Custom Branding on Print",          included: true },
      { label: "Priority AI Caching (Faster)",      included: true },
    ],
    cta: "Start free trial",
    ctaHref: `${APP_BASE_URL}/signup`,
    ctaExternal: true,
  },
];

function priceFor(plan: Plan, currency: CurrencyCode): string {
  if (plan.priceUsd === null) return plan.priceLabel ?? "—";
  const amount = convertFromUSD(plan.priceUsd, currency);
  return formatCurrency(amount, currency);
}

export default function Pricing() {
  const { currency } = useCurrencySettings();

  return (
    <>
      <Helmet>
        <title>Pricing — Deedo</title>
        <meta
          name="description"
          content="Simple, transparent pricing — one plan at $40/month per listing. No hidden fees, cancel anytime."
        />
        <link rel="canonical" href={`${MARKETING_BASE}/pricing`} />
        <meta property="og:title" content="Pricing — Deedo" />
        <meta property="og:description" content="Simple, transparent pricing. Cancel anytime." />
        <meta property="og:url" content={`${MARKETING_BASE}/pricing`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <section className="bg-gray-50 pt-20 pb-10">
        <div className="mx-auto max-w-4xl px-4 text-center lg:px-8">
          <h1 className="text-4xl font-extrabold tracking-tight text-[var(--brand-primary)] lg:text-5xl">
            Simple, Transparent Pricing
          </h1>
          <p className="mt-4 text-lg text-gray-600">
            One simple plan. No hidden fees, cancel anytime.
          </p>
          <div className="mt-6 flex justify-center">
            <CurrencySelector label="Display in:" />
          </div>
        </div>
      </section>

      <section className="bg-gray-50 pb-24">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          {/* REL_1.1.15D E-121 (S-1211) — single plan, centered (was a 3-col grid). */}
          <div className="mx-auto max-w-md">
            {PLANS.map((plan) => (
              <PricingCard key={plan.name} plan={plan} currency={currency} />
            ))}
          </div>

          <div className="mt-16 text-center text-sm text-gray-600">
            Have questions? The AI doesn&apos;t just know houses, it knows Deedo.
            <br />
            <Link to="/contact" className="mt-1 inline-block font-semibold text-[var(--brand-primary)] hover:underline">
              Check out our full FAQ.
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function PricingCard({ plan, currency }: { plan: Plan; currency: CurrencyCode }) {
  const displayPrice = priceFor(plan, currency);
  const ctaElement = plan.ctaExternal ? (
    <a
      href={plan.ctaHref}
      className={
        plan.featured
          ? "mt-8 block rounded-lg bg-[var(--brand-secondary)] py-3 text-center text-sm font-semibold text-[var(--brand-primary)] transition-colors hover:bg-[var(--brand-secondary-light)]"
          : "mt-8 block rounded-lg border border-[var(--brand-primary)] py-3 text-center text-sm font-semibold text-[var(--brand-primary)] transition-colors hover:bg-[var(--brand-primary)] hover:text-white"
      }
      aria-label={`${plan.cta} (${plan.name})`}
    >
      {plan.cta}
    </a>
  ) : (
    <Link
      to={plan.ctaHref}
      className={
        plan.featured
          ? "mt-8 block rounded-lg bg-[var(--brand-secondary)] py-3 text-center text-sm font-semibold text-[var(--brand-primary)] transition-colors hover:bg-[var(--brand-secondary-light)]"
          : "mt-8 block rounded-lg border border-[var(--brand-primary)] py-3 text-center text-sm font-semibold text-[var(--brand-primary)] transition-colors hover:bg-[var(--brand-primary)] hover:text-white"
      }
      aria-label={`${plan.cta} (${plan.name})`}
    >
      {plan.cta}
    </Link>
  );

  if (plan.featured) {
    return (
      <div className="relative">
        <span className="absolute left-1/2 -top-3 -translate-x-1/2 rounded-full bg-[var(--brand-secondary)] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[var(--brand-primary)]">
          Most Popular
        </span>
        <article className="flex h-full flex-col rounded-2xl bg-[var(--brand-primary)] p-8 text-white shadow-2xl ring-1 ring-[var(--brand-secondary)]/40">
          <h3 className="text-2xl font-bold">{plan.name}</h3>
          <p className="mt-2 text-sm text-blue-200">{plan.desc}</p>
          <div className="mt-6 flex items-baseline gap-1.5">
            <span className="text-5xl font-extrabold">{displayPrice}</span>
            {plan.unit && <span className="text-sm text-blue-200">{plan.unit}</span>}
          </div>
          <ul className="mt-6 flex-1 space-y-3">
            {plan.features.map((f) => (
              <li key={f.label} className="flex items-center gap-2.5 text-sm">
                {f.included ? (
                  <Check className="h-4 w-4 shrink-0 text-[var(--brand-secondary)]" aria-hidden="true" />
                ) : (
                  <Minus className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
                )}
                <span className={f.included ? "" : "text-gray-500 line-through"}>{f.label}</span>
              </li>
            ))}
          </ul>
          {ctaElement}
        </article>
      </div>
    );
  }

  return (
    <article className="flex h-full flex-col rounded-2xl bg-white p-8 shadow-sm">
      <h3 className="text-2xl font-bold text-[var(--brand-primary)]">{plan.name}</h3>
      <p className="mt-2 text-sm text-gray-500">{plan.desc}</p>
      <div className="mt-6 flex items-baseline gap-1.5">
        <span className="text-5xl font-extrabold text-[var(--brand-primary)]">{displayPrice}</span>
        {plan.unit && <span className="text-sm text-gray-500">{plan.unit}</span>}
      </div>
      <ul className="mt-6 flex-1 space-y-3">
        {plan.features.map((f) => (
          <li key={f.label} className="flex items-center gap-2.5 text-sm">
            {f.included ? (
              <Check className="h-4 w-4 shrink-0 text-[var(--brand-accent)]" aria-hidden="true" />
            ) : (
              <Minus className="h-4 w-4 shrink-0 text-gray-300" aria-hidden="true" />
            )}
            <span className={f.included ? "text-gray-700" : "text-gray-400 line-through"}>
              {f.label}
            </span>
          </li>
        ))}
      </ul>
      {ctaElement}
    </article>
  );
}
